import { History } from 'lucide-react';
import { describeTeamEvent } from '@mustauto/types/permissions';
import { Card, CardContent, EmptyState, ErrorState } from '@mustauto/ui/web';
import { useTeamHistory } from '@/hooks/useTeamMembers';

function when(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `hoje ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return `ontem ${time}`;
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${time}`;
}

/**
 * Histórico da equipe: toda mudança de papel, de permissão, convite e remoção,
 * com quem fez. É o que responde "quem tirou o desconto do Carlos?".
 */
export function TeamHistoryTab() {
  const { data, isLoading, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError } = useTeamHistory(true);

  if (isError && !data) {
    return <ErrorState title="Não foi possível carregar o histórico" error={error} onRetry={() => refetch()} />;
  }
  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const events = data.pages.flatMap((p) => p.events);
  if (events.length === 0) {
    return (
      <Card>
        <CardContent>
          <EmptyState
            icon={<History size={28} />}
            title="Nada mudou na equipe ainda"
            description="Convites, entradas, mudanças de papel e de permissão aparecem aqui, com quem fez."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="divide-y divide-border p-0">
          {events.map((e) => {
            const { title, detail } = describeTeamEvent(e);
            return (
              <div key={e.id} className="px-4 py-3">
                <p className="text-sm font-medium">{title}</p>
                <p className="text-xs text-muted-foreground">
                  {detail ? `${detail} · ` : ''}
                  {when(e.createdAt)}
                </p>
              </div>
            );
          })}
        </CardContent>
      </Card>
      {isFetchNextPageError && (
        <p className="text-center text-sm text-destructive">Não foi possível carregar os mais antigos. Tente de novo.</p>
      )}
      {hasNextPage && (
        <button
          type="button"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className="w-full rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
        >
          {isFetchingNextPage ? 'Carregando...' : 'Ver mais antigos'}
        </button>
      )}
    </div>
  );
}
